// src/components/strumenti/SezioneCavo.js
// Calcolo semplificato sezione cavo: verifica sia il criterio di PORTATA (con fattori di
// correzione per temperatura e raggruppamento circuiti) sia quello di CADUTA DI TENSIONE,
// e propone la sezione commerciale che soddisfa il più gravoso dei due.
//
// I valori di portata di base sono valori di riferimento comunemente pubblicati, derivati
// dalla norma IEC 60364-5-52 / CEI-UNEL 35024-1 (conduttori in rame, 3 conduttori caricati,
// temperatura di riferimento 30°C) — provengono dalla formazione tecnica di chi ha scritto
// questo strumento, NON da una consultazione diretta della tabella ufficiale aggiornata.
// Da verificare sempre sul datasheet del cavo o sulla norma ufficiale prima di un impiego reale.
import React, { useState } from "react";
import { RefreshCw, Printer } from "lucide-react";
import { METODI_POSA } from "../../utils/portataCaviUtils";
import { leggiNumero } from "../../utils/numeriStrumenti";

const inp = { padding:"9px 10px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:13, fontFamily:"'Inter',sans-serif", outline:"none", width:"100%" };
const lbl = { fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 };

const SEZIONI = [1.5,2.5,4,6,10,16,25,35,50,70,95,120,150,185,240];

// Portata di base — ora presa dalla tabella condivisa e verificata (portataCaviUtils.js),
// distinta correttamente tra 2 e 3 conduttori caricati in base al tipo di linea scelto sotto.

// Fattore di correzione per temperatura ambiente (riferimento 30°C)
const FATT_TEMP = {
  pvc:  {10:1.22,15:1.17,20:1.12,25:1.06,30:1.00,35:0.94,40:0.87,45:0.79,50:0.71,55:0.61,60:0.50},
  xlpe: {10:1.15,15:1.12,20:1.08,25:1.04,30:1.00,35:0.96,40:0.91,45:0.87,50:0.82,55:0.76,60:0.71,65:0.65,70:0.58},
};

// Fattore di correzione per raggruppamento circuiti (cavi/circuiti multipolari a contatto)
const FATT_RAGGRUPPAMENTO = {1:1.00,2:0.80,3:0.70,4:0.65,5:0.60,6:0.57,7:0.54,8:0.52,9:0.50,10:0.48,11:0.46,12:0.45};

const RESISTIVITA = { rame: 0.0178, alluminio: 0.0286 };
const DERATING_ALLUMINIO = 0.78; // approssimazione indicativa rispetto al rame a parità di mm²

// Metodo di installazione IEC 60364-5-52 per ogni posa (W442: prima tre pose usavano il metodo B,
// che dà portate più alte del dovuto: in parete isolante il metodo è A1/A2, la canalina con cavo
// multipolare è B2). La posa interrata (metodo D) non è nella tabella: si usa B, con avviso.
const NOME_METODO = { a:"A1", a2:"A2", b:"B1", b2:"B2", c:"C", e:"E" };
const POSE = [
  { id:"tubo_incassato_isolante", label:"Conduttori in tubo, incassato in parete termicamente isolante", rif:"a" },
  { id:"tubo_vista", label:"Conduttori in tubo protettivo a vista, su parete non isolante", rif:"b" },
  { id:"multipolare_tubo_incassato", label:"Cavo multipolare in tubo protettivo, incassato a parete", rif:"a2" },
  { id:"multipolare_canalina", label:"Cavo multipolare in canalina, a parete o soffitto", rif:"b2" },
  { id:"multipolare_a_vista", label:"Cavo multipolare posato direttamente su parete o soffitto (in vista)", rif:"c" },
  { id:"controsoffitto", label:"Cavo multipolare in controsoffitto o pavimento sopraelevato", rif:"c" },
  { id:"passerella_non_perforata", label:"Cavo multipolare su passerella non perforata", rif:"c" },
  { id:"passerella_perforata", label:"Cavo multipolare su passerella perforata", rif:"e" },
  { id:"mensole_traversini", label:"Cavo multipolare su mensole o passerella a traversini", rif:"e" },
  { id:"aria_libera", label:"Cavo o conduttore in aria libera, ben distanziato dalla parete", rif:"e" },
  { id:"interrato_tubo", label:"Cavo interrato, in tubo/cavidotto", rif:"b", interrato:true },
  { id:"interrato_diretto", label:"Cavo interrato, posa diretta senza tubo", rif:"b", interrato:true },
];

export default function SezioneCavo() {
  const [tipoLinea, setTipoLinea] = useState("trifase");
  const [materiale, setMateriale] = useState("rame");
  const [isolante, setIsolante] = useState("pvc");
  const [posa, setPosa] = useState("tubo_incassato_isolante");
  const [temperatura, setTemperatura] = useState("30");
  const [circuiti, setCircuiti] = useState("1");
  const [corrente, setCorrente] = useState("");
  const [lunghezza, setLunghezza] = useState("");
  const [tensione, setTensione] = useState("400");
  const [cosphi, setCosphi] = useState("0.9");
  const [cdtMax, setCdtMax] = useState("4");
  const [denominazioneImpianto, setDenominazioneImpianto] = useState("");
  const [clienteUbicazione, setClienteUbicazione] = useState("");
  const [redattoDa, setRedattoDa] = useState("");

  const I = leggiNumero(corrente)||0;
  const L = leggiNumero(lunghezza)||0;
  const V = leggiNumero(tensione)||0;
  const cf = leggiNumero(cosphi)||1;
  const perc = leggiNumero(cdtMax)||4;
  const temp = parseInt(temperatura)||30;
  const nCirc = Math.min(parseInt(circuiti)||1, 12);
  const b = tipoLinea==="trifase" ? Math.sqrt(3) : 2;
  const rho = RESISTIVITA[materiale];

  // Fattori di correzione
  const kTemp = FATT_TEMP[isolante][temp] ?? 1;
  const kRaggr = FATT_RAGGRUPPAMENTO[nCirc] ?? 1;
  const kMateriale = materiale==="alluminio" ? DERATING_ALLUMINIO : 1;
  const posaSelezionata = POSE.find(p => p.id === posa) || POSE[0];
  const rifPortata = posaSelezionata.rif;

  // Criterio 1: PORTATA — cerca la prima sezione commerciale la cui portata corretta >= corrente richiesta
  const nConduttoriCaricati = tipoLinea === "trifase" ? 3 : 2;
  let sezionePortata = null;
  if (I>0) {
    for (let i=0;i<SEZIONI.length;i++) {
      const base = METODI_POSA[rifPortata]?.[isolante]?.[String(SEZIONI[i])]?.[nConduttoriCaricati];
      if (!base) continue; // norma non copre questa sezione per questo metodo/isolante
      const portataCorretta = base * kTemp * kRaggr * kMateriale;
      if (portataCorretta >= I) { sezionePortata = SEZIONI[i]; break; }
    }
  }

  // Criterio 2: CADUTA DI TENSIONE
  const puoCalcolareCdt = I>0 && L>0 && V>0 && perc>0;
  const deltaVAmmessa = puoCalcolareCdt ? V*(perc/100) : null;
  const sezioneMinimaCdt = puoCalcolareCdt ? (b*rho*L*I*cf)/deltaVAmmessa : null;
  const sezioneCdt = sezioneMinimaCdt!==null ? (SEZIONI.find(s=>s>=sezioneMinimaCdt) || null) : null;

  const sezioneFinale = (sezionePortata!==null || sezioneCdt!==null)
    ? Math.max(sezionePortata||0, sezioneCdt||0) : null;
  const criterioVincolante = sezioneFinale!==null
    ? (sezioneFinale===sezionePortata && sezioneFinale>=(sezioneCdt||0) ? "portata" : "caduta di tensione")
    : null;

  const azzera = () => { setCorrente(""); setLunghezza(""); };

  const stampaPDF = () => {
    const win = window.open("", "_blank");
    const oggi = new Date().toLocaleDateString("it-IT", { day:"numeric", month:"long", year:"numeric" });
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Calcolo Sezione Cavo — ${denominazioneImpianto || "Senza denominazione"}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;color:#111;padding:14mm;font-size:12px;}
      @media screen{body{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4;margin:14mm;}
      h1{font-size:18px;color:#5b21b6;margin-bottom:2px;}
      .sub{font-size:11px;color:#6b7280;margin-bottom:16px;}
      table{width:100%;border-collapse:collapse;margin-bottom:14px;}
      td,th{border:1px solid #e5e7eb;padding:6px 10px;text-align:left;font-size:12px;}
      th{background:#f9fafb;width:45%;color:#374151;}
      .risultato{background:#faf5ff;border:1px solid #e9d5ff;border-radius:8px;padding:16px;text-align:center;margin-bottom:14px;}
      .risultato .val{font-size:28px;font-weight:800;color:#7c3aed;}
      .avviso{background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:10px 12px;font-size:10.5px;color:#92400e;margin-bottom:14px;}
      .firma{margin-top:40px;display:flex;justify-content:space-between;font-size:11px;}
      .firma div{border-top:1px solid #9ca3af;width:200px;padding-top:4px;text-align:center;color:#6b7280;}
    </style></head><body>
      <h1>📐 Calcolo Sezione Cavo</h1>
      <div class="sub">Nexiora — Strumenti Tecnici · Generato il ${oggi}</div>

      <table>
        <tr><th>Denominazione impianto</th><td>${denominazioneImpianto || "—"}</td></tr>
        <tr><th>Cliente / Ubicazione</th><td>${clienteUbicazione || "—"}</td></tr>
        <tr><th>Redatto da</th><td>${redattoDa || "—"}</td></tr>
      </table>

      <table>
        <tr><th>Tipo linea</th><td>${tipoLinea==="trifase"?"Trifase":"Monofase"}</td></tr>
        <tr><th>Materiale conduttore</th><td>${materiale==="rame"?"Rame":"Alluminio"}</td></tr>
        <tr><th>Isolante</th><td>${isolante==="pvc"?"PVC":"XLPE / EPR"}</td></tr>
        <tr><th>Tipo di posa</th><td>${posaSelezionata.label}</td></tr>
        <tr><th>Metodo di installazione (IEC 60364-5-52)</th><td>${posaSelezionata.interrato ? "B1 (approssimazione: il metodo D non è nella tabella)" : NOME_METODO[rifPortata]}</td></tr>
        <tr><th>Temperatura ambiente</th><td>${temperatura} °C</td></tr>
        <tr><th>N° circuiti nello stesso condotto</th><td>${nCirc}</td></tr>
        <tr><th>Corrente di impiego</th><td>${I} A</td></tr>
        <tr><th>Lunghezza linea</th><td>${L} m</td></tr>
        <tr><th>Tensione</th><td>${V} V</td></tr>
        <tr><th>cos φ</th><td>${cf}</td></tr>
        <tr><th>Caduta di tensione massima ammessa</th><td>${perc}%</td></tr>
      </table>

      <div class="risultato">
        <div style="font-size:11px;color:#9ca3af;margin-bottom:4px;">Sezione per portata: <strong>${sezionePortata?sezionePortata+" mm²":"—"}</strong> · Sezione per caduta di tensione: <strong>${sezioneCdt?sezioneCdt+" mm²":"—"}</strong></div>
        <div style="font-size:11px;color:#9ca3af;margin-bottom:6px;">Sezione commerciale consigliata</div>
        <div class="val">${sezioneFinale ? sezioneFinale+" mm²" : "—"}</div>
        ${criterioVincolante ? `<div style="font-size:11px;color:#9ca3af;margin-top:4px;">Criterio vincolante: ${criterioVincolante}</div>` : ""}
      </div>

      <div class="avviso">
        ⚠️ Le portate di base utilizzate sono valori di riferimento tecnico standard (IEC 60364-5-52 / CEI-UNEL 35024), non una consultazione diretta della norma ufficiale aggiornata. Calcolo semplificato: non tiene conto di protezione da sovracorrente, coordinamento con l'interruttore o altre verifiche CEI 64-8. Non sostituisce una verifica progettuale. Per la posa interrata l'approssimazione è ulteriormente ridotta (non considera resistività del terreno, profondità, umidità).
      </div>

      <div class="firma">
        <div>Redatto da</div>
        <div>Data e firma</div>
      </div>
      
    </body></html>`);
    win.document.close();
    setTimeout(()=>{
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
  };

  return (
    <div>
      <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:"9px 11px", marginBottom:16, fontSize:10.5, color:"#92400e", lineHeight:1.5 }}>
        ⚠️ Le portate di base usate qui sono valori di riferimento tecnico standard, non una consultazione diretta della norma ufficiale aggiornata. Verificare sempre sul datasheet del cavo prima di un utilizzo reale.
      </div>

      <div style={{ background:"#f9fafb", border:"1px solid #e5e7eb", borderRadius:8, padding:12, marginBottom:16 }}>
        <div style={{ marginBottom:8 }}>
          <label style={lbl}>Denominazione impianto (necessaria per la stampa)</label>
          <input value={denominazioneImpianto} onChange={e=>setDenominazioneImpianto(e.target.value)} style={inp} placeholder="Es. Quadro QE1 - Linea illuminazione capannone"/>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          <div>
            <label style={lbl}>Cliente / Ubicazione (facoltativo)</label>
            <input value={clienteUbicazione} onChange={e=>setClienteUbicazione(e.target.value)} style={inp} placeholder="Es. Rossi Srl - Bologna"/>
          </div>
          <div>
            <label style={lbl}>Redatto da (facoltativo)</label>
            <input value={redattoDa} onChange={e=>setRedattoDa(e.target.value)} style={inp} placeholder="Es. Mario Rossi"/>
          </div>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
        <div>
          <label style={lbl}>Tipo linea</label>
          <select value={tipoLinea} onChange={e=>setTipoLinea(e.target.value)} style={inp}>
            <option value="trifase">Trifase</option>
            <option value="monofase">Monofase</option>
          </select>
        </div>
        <div>
          <label style={lbl}>Materiale conduttore</label>
          <select value={materiale} onChange={e=>setMateriale(e.target.value)} style={inp}>
            <option value="rame">Rame</option>
            <option value="alluminio">Alluminio</option>
          </select>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
        <div>
          <label style={lbl}>Isolante</label>
          <select value={isolante} onChange={e=>setIsolante(e.target.value)} style={inp}>
            <option value="pvc">PVC</option>
            <option value="xlpe">XLPE / EPR</option>
          </select>
        </div>
        <div>
          <label style={lbl}>Tipo di posa</label>
          <select value={posa} onChange={e=>setPosa(e.target.value)} style={inp}>
            {POSE.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
      </div>

      <div style={{ fontSize:11, color:"#6b7280", marginTop:-4, marginBottom:10 }}>Metodo di installazione usato (IEC 60364-5-52): <strong>{posaSelezionata.interrato ? "B1 (al posto di D)" : NOME_METODO[rifPortata]}</strong> · {nConduttoriCaricati} conduttori caricati</div>
      {(cf > 1 || cf <= 0) && <div role="alert" style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"7px 10px", marginBottom:10, fontSize:11, color:"#991b1b" }}>Il cos φ deve essere tra 0 e 1: controlla il valore scritto.</div>}
      {posaSelezionata.interrato && (
        <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:8, padding:"9px 11px", marginBottom:16, fontSize:10.5, color:"#991b1b", lineHeight:1.5 }}>
          ⚠️ Il metodo D (posa interrata) non è nella tabella: la portata è calcolata con il metodo B1 come approssimazione. Per la posa interrata la portata reale dipende da resistività del terreno, profondità e umidità — parametri che questo strumento non considera. Il valore qui calcolato è un'approssimazione ancora meno affidabile del solito: verificare sempre con la tabella specifica per posa interrata prima di un uso reale.
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
        <div>
          <label style={lbl}>Temperatura ambiente (°C)</label>
          <input value={temperatura} onChange={e=>setTemperatura(e.target.value)} style={inp}/>
        </div>
        <div>
          <label style={lbl}>N° circuiti nello stesso condotto</label>
          <input value={circuiti} onChange={e=>setCircuiti(e.target.value)} style={inp}/>
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
        <div>
          <label style={lbl}>Corrente di impiego (A)</label>
          <input value={corrente} onChange={e=>setCorrente(e.target.value)} style={inp} placeholder="Es. 32"/>
        </div>
        <div>
          <label style={lbl}>Lunghezza linea (m)</label>
          <input value={lunghezza} onChange={e=>setLunghezza(e.target.value)} style={inp} placeholder="Es. 50"/>
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:16 }}>
        <div>
          <label style={lbl}>Tensione (V)</label>
          <input value={tensione} onChange={e=>setTensione(e.target.value)} style={inp}/>
        </div>
        <div>
          <label style={lbl}>cos φ</label>
          <input value={cosphi} onChange={e=>setCosphi(e.target.value)} style={inp}/>
        </div>
        <div>
          <label style={lbl}>Cad. tens. max (%)</label>
          <input value={cdtMax} onChange={e=>setCdtMax(e.target.value)} style={inp}/>
        </div>
      </div>

      <div style={{ background:"#faf5ff", border:"1px solid #e9d5ff", borderRadius:10, padding:"14px 16px" }}>
        <div style={{ display:"flex", justifyContent:"space-around", marginBottom:10, fontSize:11, color:"#6b7280" }}>
          <div style={{textAlign:"center"}}>
            <div>Per portata</div>
            <div style={{fontWeight:700, color:"#374151", fontSize:14}}>{sezionePortata ? `${sezionePortata} mm²` : "—"}</div>
          </div>
          <div style={{textAlign:"center"}}>
            <div>Per caduta tensione</div>
            <div style={{fontWeight:700, color:"#374151", fontSize:14}}>{sezioneCdt ? `${sezioneCdt} mm²` : "—"}</div>
          </div>
        </div>
        <div style={{ textAlign:"center", borderTop:"1px solid #e9d5ff", paddingTop:10 }}>
          <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, marginBottom:2 }}>Sezione commerciale consigliata</div>
          <div style={{ fontSize:26, fontWeight:800, color:"#7c3aed" }}>
            {sezioneFinale ? `${sezioneFinale} mm²` : "—"}
          </div>
          {criterioVincolante && (
            <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>Criterio vincolante: {criterioVincolante}</div>
          )}
        </div>
      </div>
      <div style={{ fontSize:10, color:"#9ca3af", marginTop:8, textAlign:"center" }}>
        Non tiene conto di protezione da sovracorrente, coordinamento con l'interruttore o altre verifiche CEI 64-8. Non sostituisce una verifica progettuale.
      </div>

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:12 }}>
        <button onClick={()=>{azzera();setTensione("400");setCosphi("0.9");setCdtMax("4");setTipoLinea("trifase");setMateriale("rame");setIsolante("pvc");setPosa("tubo_incassato_isolante");setTemperatura("30");setCircuiti("1");setDenominazioneImpianto("");setClienteUbicazione("");setRedattoDa("");}}
          style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#9ca3af", background:"none", border:"none", cursor:"pointer" }}>
          <RefreshCw size={12}/> Azzera
        </button>
        <button onClick={stampaPDF} disabled={!denominazioneImpianto.trim()}
          title={!denominazioneImpianto.trim() ? "Inserisci la denominazione impianto per stampare" : "Stampa PDF"}
          style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:700,
            color: denominazioneImpianto.trim() ? "#7c3aed" : "#c4b5fd",
            background: denominazioneImpianto.trim() ? "#faf5ff" : "#f9fafb",
            border:`1px solid ${denominazioneImpianto.trim() ? "#e9d5ff" : "#e5e7eb"}`,
            borderRadius:8, padding:"7px 14px", cursor: denominazioneImpianto.trim() ? "pointer" : "not-allowed" }}>
          <Printer size={13}/> Stampa PDF
        </button>
      </div>
    </div>
  );
}
